import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { ProviderHeroSkeleton } from '@/components/Skeletons';
import { Tip } from '@/components/Tip';
import { TrustStrip } from '@/components/TrustBadges';
import Lightbox from '@/components/Lightbox';
import { CANCELLATION_POLICIES, describePolicy, embedUrl } from '@/lib/policies';
import { providerPrice } from '@/lib/pricing';
import {
  Star, MapPin, Clock, ShieldCheck, Heart, ChatCircle, ArrowLeft, CalendarBlank,
  CaretLeft, CaretRight, Lightning, Users, Check, ShareNetwork, Fire,
  Envelope, Phone, CalendarCheck, CheckCircle, FileText, DownloadSimple
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Clean, human labels for a service's raw price_type — so the services tab
// never shows "hourly"/"fixed"/"per_person" and matches the sidebar wording.
const SERVICE_UNIT_LABEL = {
  hourly: 'per hour',
  fixed: 'per event',
  per_person: 'per person',
  range: 'price range',
  quote: 'custom quote',
};

const CAT_IMAGES = {
  'Venues': 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&h=400&fit=crop',
  'Catering': 'https://images.unsplash.com/photo-1555244162-803834f70033?w=800&h=400&fit=crop',
  'DJs': 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&h=400&fit=crop',
  'Photographers': 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=800&h=400&fit=crop',
  'Videographers': 'https://images.unsplash.com/photo-1579632652768-6cb9dcf85912?w=800&h=400&fit=crop',
  'Bartenders': 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&h=400&fit=crop',
  'Event Decor': 'https://images.unsplash.com/photo-1478146059778-26028b07395a?w=800&h=400&fit=crop',
  'Live Bands': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=400&fit=crop',
  'Private Chefs': 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800&h=400&fit=crop',
  'Cleaners': 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=400&fit=crop',
  'Event Planners': 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=800&h=400&fit=crop',
};

// A gallery tile: the photo fills the whole box (object-cover, NO letterbox
// bars) but is cropped around its AI-detected focal point so the subject — a
// face, a DJ at the decks — stays in frame instead of the ceiling. `focus` is
// [x%, y%] from the backend's vision analysis; absent → centered as usual.
function GalleryPhoto({ src, alt, focus }) {
  if (!src) return <div className="absolute inset-0 bg-muted" aria-hidden="true" />;
  const pos = Array.isArray(focus) && focus.length === 2 ? `${focus[0]}% ${focus[1]}%` : '50% 50%';
  return (
    <img src={src} alt={alt}
      className="absolute inset-0 w-full h-full object-cover"
      style={{ objectPosition: pos }} />
  );
}

// Ask a vendor for a custom price
function QuoteModal({ provider, token, onClose }) {
  const [form, setForm] = useState({ event_date: '', event_type: '', guest_count: '', budget: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "w-full h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/quotes`, { profile_id: provider.profile_id, ...form }, { headers: { Authorization: `Bearer ${token}` } });
      setSent(true);
    } catch (err) { setError(err?.response?.data?.detail || 'Could not send your request. Please try again.'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-foreground/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} role="dialog" aria-modal="true" data-testid="quote-modal">
      <div className="bg-card rounded-2xl shadow-lift p-6 w-full max-w-md my-8" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="text-center py-6" data-testid="quote-sent">
            <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={30} weight="fill" className="text-emerald-600" />
            </div>
            <h3 className="font-display text-lg font-semibold text-foreground mb-2">Request sent!</h3>
            <p className="text-sm text-muted-foreground mb-6">{provider.business_name} will reply with a custom price. Track it in your dashboard under <span className="font-medium">Quotes</span>.</p>
            <button onClick={onClose} className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90 cursor-pointer">Done</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3 className="font-display text-lg font-semibold text-foreground mb-1">Request a custom quote</h3>
            <p className="text-xs text-muted-foreground mb-5">Tell {provider.business_name} about your event — they'll reply with a price built for you.</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Event date</label>
                <input type="date" value={form.event_date} onChange={e => set('event_date', e.target.value)} className={inputCls} data-testid="quote-date" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Guests</label>
                <input type="text" placeholder="e.g. 80" value={form.guest_count} onChange={e => set('guest_count', e.target.value)} className={inputCls} data-testid="quote-guests" />
              </div>
            </div>
            <label className="block text-xs font-medium text-foreground mb-1">What's the event?</label>
            <input type="text" required placeholder="Wedding reception, office party…" value={form.event_type} onChange={e => set('event_type', e.target.value)} className={`${inputCls} mb-3`} data-testid="quote-type" />
            <label className="block text-xs font-medium text-foreground mb-1">Budget (optional)</label>
            <input type="text" placeholder="e.g. $2,000–3,000" value={form.budget} onChange={e => set('budget', e.target.value)} className={`${inputCls} mb-3`} data-testid="quote-budget" />
            <label className="block text-xs font-medium text-foreground mb-1">Details</label>
            <textarea rows={3} placeholder="Times, location, style, anything that helps them price it…" value={form.message} onChange={e => set('message', e.target.value)}
              className="w-full rounded-xl border border-border bg-card px-3 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none mb-3" data-testid="quote-message" />
            {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-secondary/60 btn-anim cursor-pointer">Cancel</button>
              <button type="submit" disabled={busy} className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-50 cursor-pointer" data-testid="quote-submit">
                {busy ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


// ─── Availability Calendar ───
function AvailabilityCalendar({ providerUserId, profileId, serviceId }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState({ slots: [], booked_dates: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const ms = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
    axios.get(`${API}/availability/${providerUserId}?month=${ms}`).then(r => setAvailability(r.data)).catch(err => console.error('Avail error:', err));
  }, [providerUserId, currentMonth]);

  const year = currentMonth.getFullYear(), month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const blocked = new Set((availability.slots || []).filter(s => !s.available).map(s => s.date));
  const booked = new Set(availability.booked_dates || []);

  const getStatus = (day) => {
    const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (new Date(year, month, day) < today) return 'past';
    if (blocked.has(ds)) return 'blocked';
    if (booked.has(ds)) return 'booked';
    return 'open';
  };

  const times = ['09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00'];

  const prettyDate = selectedDate
    ? new Date(selectedDate + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : '';

  return (
    <div data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-display font-semibold text-foreground flex items-center gap-1.5">
          <CalendarBlank size={15} className="text-primary" /> Check availability
        </span>
        <div className="flex items-center gap-0.5">
          <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} aria-label="Previous month"
            className="w-8 h-8 flex items-center justify-center hover:bg-secondary/70 rounded-lg btn-anim cursor-pointer">
            <CaretLeft size={13} weight="bold" className="text-muted-foreground" />
          </button>
          <span className="text-xs font-semibold text-foreground w-28 text-center">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
          <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} aria-label="Next month"
            className="w-8 h-8 flex items-center justify-center hover:bg-secondary/70 rounded-lg btn-anim cursor-pointer">
            <CaretRight size={13} weight="bold" className="text-muted-foreground" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {['S','M','T','W','T','F','S'].map((d, i) => <div key={`h${i}`} className="text-[10px] font-semibold text-muted-foreground/70 py-1">{d}</div>)}
        {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const st = getStatus(day);
          const sel = selectedDate === ds;
          const ok = st === 'open';
          return (
            <button key={day} disabled={!ok} onClick={() => ok && setSelectedDate(sel ? null : ds)}
              aria-label={ok ? `Select ${ds}` : undefined}
              className={`aspect-square flex items-center justify-center text-[11px] rounded-lg transition-all duration-150 tabular-nums ${
                sel ? 'bg-primary text-primary-foreground font-bold shadow-soft scale-105'
                : st === 'past' ? 'text-border'
                : st === 'blocked' || st === 'booked' ? 'text-muted-foreground/35 line-through decoration-accent-coral/60'
                : 'text-foreground font-medium hover:bg-primary/10 hover:text-primary cursor-pointer'}`}>
              {day}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-4 mt-2.5 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-primary" /> Selected</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-secondary border border-border" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-accent-coral/40" /> Unavailable</span>
      </div>

      {selectedDate && (
        <div className="mt-3 pt-3 border-t border-border/70 space-y-2.5 fade-up">
          <p className="text-xs font-semibold text-foreground">What time on {new Date(selectedDate + 'T12:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}?</p>
          <div className="grid grid-cols-4 gap-1.5">
            {times.map(t => (
              <button key={t} onClick={() => setSelectedTime(t)}
                className={`h-9 text-[11px] font-medium rounded-lg border btn-anim cursor-pointer tabular-nums ${selectedTime === t ? 'bg-primary text-primary-foreground border-primary shadow-soft' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-secondary/40'}`}>{t}</button>
            ))}
          </div>
          <button onClick={() => { if (!user) navigate('/auth'); else navigate(`/checkout/${profileId}/${serviceId}?date=${selectedDate}&time=${selectedTime || '14:00'}`); }}
            disabled={!selectedTime} className="w-full bg-primary text-primary-foreground py-3 min-h-[44px] text-xs font-semibold rounded-xl btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer" data-testid="calendar-book-button">
            {selectedTime ? `Book ${prettyDate} at ${selectedTime}` : 'Pick a time to book'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main Profile Page ───
export default function ProviderProfilePage() {
  const { profileId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFav, setIsFav] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    ['about', 'services', 'reviews', 'faq'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'about'
  );

  const fetchProvider = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/providers/${profileId}`);
      setProvider(res.data);
    } catch (err) { console.error('Provider error:', err); }
    finally { setLoading(false); }
  }, [profileId]);

  useEffect(() => { fetchProvider(); }, [fetchProvider]);

  const toggleFav = async () => {
    if (!user) { navigate('/auth'); return; }
    try { const r = await axios.post(`${API}/favorites/${provider.user_id}`, {}, { headers: { Authorization: `Bearer ${token}` } }); setIsFav(r.data.favorited); } catch (e) { console.error('Fav error:', e); }
  };

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><ProviderHeroSkeleton /></div></div>;
  if (!provider) return <div className="min-h-screen bg-background"><Navbar /><div className="max-w-lg mx-auto px-6 py-24 text-center"><p className="text-muted-foreground">Provider not found.</p></div></div>;

  const name = provider.business_name || '';
  const heroImg = CAT_IMAGES[provider.category] || CAT_IMAGES['Venues'];
  // The vendor's own photos drive the gallery — cover photo leads, then portfolio
  // shots. The stock category image is only a fallback when they've uploaded nothing.
  const ownPhotos = [
    ...(provider.cover_image ? [provider.cover_image] : []),
    ...((provider.portfolio_images || []).filter(p => p !== provider.cover_image)),
  ];
  const gallery = ownPhotos.length > 0 ? ownPhotos : [heroImg];
  // Guard against non-string / unsafe values: only our own relative /api paths
  // get the backend prefix; anything else (or a malformed doc) is dropped, so a
  // bad entry can never crash the render or inject a javascript: href.
  const imgSrc = (s) => (typeof s === 'string' && s.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${s}` : '');
  // AI-detected focal point for a photo (keyed by its raw /api/files path), so
  // the crop stays on the subject. Undefined → the tile centers the crop.
  const focusOf = (s) => (provider.image_focus || {})[s];
  const policy = CANCELLATION_POLICIES[provider.cancellation_policy] || CANCELLATION_POLICIES.flexible;
  // A portfolio video is either an uploaded file (/api/files/…, played with a
  // <video> tag) or a YouTube/Vimeo link (embedded in an iframe).
  const videos = (provider.portfolio_videos || []).map(v => {
    if (String(v).startsWith('/api/files/')) return { type: 'file', src: imgSrc(v) };
    const embed = embedUrl(v);
    return embed ? { type: 'embed', src: embed } : null;
  }).filter(Boolean);
  // Real social proof only — no fabricated numbers (see Trust page: "No fake ratings, ever").
  const bookCount = provider.completed_bookings || 0;
  const recPct = (provider.reviews && provider.reviews.length >= 3)
    ? Math.round(100 * provider.reviews.filter(r => (r.rating || 0) >= 4).length / provider.reviews.length)
    : null;

  return (
    <div className="min-h-screen bg-background" data-testid="provider-profile-page">
      <Navbar />

      {/* ═══ Hero Gallery ═══ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2">
        <Link to="/browse" className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors duration-200 mb-4" data-testid="back-to-browse">
          <ArrowLeft size={14} /> Back to Browse
        </Link>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Photos fill each tile (no bars) but are cropped around their
            AI-detected focal point so the subject stays in frame. The layout
            adapts to photo count: 1 → full width; 2 → side-by-side; 3+ → the
            classic big-left with two stacked on the right + a "View all" tile. */}
        {gallery.length === 1 ? (
          <div className="rounded-2xl overflow-hidden h-[320px] md:h-[440px] shadow-soft fade-up" data-testid="hero-gallery">
            <button type="button" onClick={() => setLightbox(0)} aria-label={`Open photo of ${name}`}
              className="relative block w-full h-full cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary/50">
              <GalleryPhoto src={imgSrc(gallery[0])} alt={name} focus={focusOf(gallery[0])} />
            </button>
          </div>
        ) : gallery.length === 2 ? (
          <div className="grid grid-cols-2 gap-2 rounded-2xl overflow-hidden h-[320px] md:h-[380px] shadow-soft fade-up" data-testid="hero-gallery">
            {[0, 1].map((idx) => (
              <button key={idx} type="button" onClick={() => setLightbox(idx)} aria-label={`Open photo ${idx + 1} of ${name}`}
                className="relative cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary/50">
                <GalleryPhoto src={imgSrc(gallery[idx])} alt={`${name} ${idx + 1}`} focus={focusOf(gallery[idx])} />
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 rounded-2xl overflow-hidden h-[320px] md:h-[380px] shadow-soft fade-up" data-testid="hero-gallery">
            <button type="button" onClick={() => setLightbox(0)} aria-label={`Open photos of ${name}`}
              className="col-span-2 relative cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary/50">
              <GalleryPhoto src={imgSrc(gallery[0])} alt={name} focus={focusOf(gallery[0])} />
            </button>
            <div className="grid grid-rows-2 gap-2">
              <button type="button" onClick={() => setLightbox(1)} aria-label="Open photo 2"
                className="relative cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary/50">
                <GalleryPhoto src={imgSrc(gallery[1])} alt={`${name} 2`} focus={focusOf(gallery[1])} />
              </button>
              <button type="button" onClick={() => setLightbox(2)} aria-label={`View all ${gallery.length} photos`}
                className="relative cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary/50" data-testid="view-all-photos">
                <GalleryPhoto src={imgSrc(gallery[2])} alt={`${name} 3`} focus={focusOf(gallery[2])} />
                {gallery.length > 3 && (
                  <div className="absolute inset-0 bg-foreground/40 flex items-center justify-center z-10">
                    <span className="text-background text-sm font-medium">View all {gallery.length} photos</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        )}
        {lightbox !== null && (
          <Lightbox images={gallery} startIndex={lightbox} onClose={() => setLightbox(null)} alt={name} />
        )}
        {quoteOpen && (
          <QuoteModal provider={provider} token={token} onClose={() => setQuoteOpen(false)} />
        )}

        {/* Popularity banner */}
        <div className="mt-4 bg-secondary/70 border border-primary/15 rounded-2xl px-5 py-3.5 flex items-center justify-between fade-up" style={{ animationDelay: '50ms' }} data-testid="popularity-banner">
          <div className="flex items-center gap-2">
            <Fire size={16} weight="fill" className="text-accent-coral" />
            <span className="text-sm text-foreground">This provider is popular with customers. <Link to={user ? `/messages/${provider.user_id}` : '/auth'} className="text-primary font-medium hover:underline underline-offset-2">Start a conversation</Link></span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleFav} className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary btn-anim cursor-pointer px-2 py-2 min-h-[44px]" data-testid="favorite-button">
              <Heart size={16} weight={isFav ? 'fill' : 'regular'} className={isFav ? 'text-accent-coral' : ''} /> Save
            </button>
            <button className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary btn-anim cursor-pointer px-2 py-2 min-h-[44px]"><ShareNetwork size={16} /> Share</button>
          </div>
        </div>

        {/* Special offer banner */}
        {provider.special_offer && (
          <div className="mt-3 bg-accent-coral/10 border border-accent-coral/30 rounded-2xl px-5 py-3 flex items-center gap-2.5 fade-up" data-testid="special-offer-banner">
            <Fire size={16} weight="fill" className="text-accent-coral shrink-0" />
            <span className="text-sm font-medium text-foreground">{provider.special_offer}</span>
            <span className="text-[10px] uppercase tracking-wider text-accent-coral font-semibold ml-auto shrink-0">Special offer</span>
          </div>
        )}
      </div>

      {/* ═══ Main Content ═══ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Left: Content */}
          <div className="md:col-span-2">
            {/* Name + Rating Header */}
            <div className="mb-8 fade-up" style={{ animationDelay: '100ms' }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium tracking-widest uppercase text-primary">{provider.category}</span>
                {provider.verified && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-primary"><ShieldCheck size={11} weight="fill" /> Verified</span>}
              </div>
              <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight text-foreground mb-3" data-testid="provider-name">{name}</h1>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => <Star key={i} size={16} weight="fill" className={i <= Math.round(provider.rating_avg) ? 'text-amber-400' : 'text-border'} />)}
                  <span className="text-sm font-semibold text-foreground ml-1 tabular-nums">{provider.rating_avg}</span>
                  <span className="text-sm text-muted-foreground">Excellent &middot; {provider.review_count} reviews</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin size={14} /> {provider.city}</div>
              </div>
            </div>

            {/* Tab navigation */}
            <div className="flex gap-1 border-b border-border mb-8 sticky top-14 glass z-10">
              {[
                { id: 'about', label: 'About' },
                { id: 'services', label: 'Services' },
                { id: 'reviews', label: `Reviews ${provider.review_count}` },
                { id: 'faq', label: 'FAQ' },
              ].map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`px-4 py-3 min-h-[44px] text-sm font-medium border-b-2 btn-anim cursor-pointer ${activeTab === t.id ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-primary'}`}
                  data-testid={`tab-${t.id}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* About Tab */}
            {activeTab === 'about' && (
              <div className="space-y-10" data-testid="about-section">
                {/* Social proof stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 text-center">
                    <Users size={18} className="mx-auto text-primary mb-1.5" />
                    <p className="text-xl font-display font-semibold text-foreground tabular-nums">{bookCount || '—'}</p>
                    <p className="text-[11px] text-muted-foreground">{bookCount ? 'Bookings completed' : 'New on Bookr'}</p>
                  </div>
                  <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 text-center">
                    <Star size={18} weight="fill" className="mx-auto text-amber-400 mb-1.5" />
                    <p className="text-xl font-display font-semibold text-foreground tabular-nums">{recPct !== null ? `${recPct}%` : (provider.rating_avg ? provider.rating_avg : '—')}</p>
                    <p className="text-[11px] text-muted-foreground">{recPct !== null ? 'Recommended' : (provider.rating_avg ? 'Avg rating' : 'Awaiting reviews')}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h2 className="text-2xl font-display font-semibold tracking-tight text-foreground mb-3">About {name}</h2>
                  <p className="text-base text-muted-foreground leading-relaxed" data-testid="provider-description">{provider.description}</p>
                  <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1"><CalendarCheck size={12} className="text-primary" /> On Bookr.ai since {new Date(provider.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                </div>

                {/* Videos */}
                {videos.length > 0 && (
                  <div data-testid="video-section">
                    <h2 className="text-2xl font-display font-semibold tracking-tight text-foreground mb-3">Videos</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {videos.map((v, i) => (
                        <div key={i} className="aspect-video rounded-2xl overflow-hidden border border-border/70 shadow-soft bg-black">
                          {v.type === 'file' ? (
                            <video src={v.src} controls className="w-full h-full" preload="metadata" />
                          ) : (
                            <iframe src={v.src} title={`${name} video ${i + 1}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen frameBorder="0" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents — menus, price sheets, press kits */}
                {(provider.documents || []).length > 0 && (
                  <div data-testid="documents-section">
                    <h2 className="text-2xl font-display font-semibold tracking-tight text-foreground mb-3">Documents</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {provider.documents.map((doc, i) => (
                        <a key={i} href={imgSrc(doc.path)} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 rounded-xl border border-border/70 bg-card px-4 py-3 hover:border-primary/40 hover:shadow-soft transition-all group">
                          <FileText size={22} weight="fill" className="text-primary shrink-0" />
                          <span className="text-sm font-medium text-foreground truncate flex-1" title={doc.name}>{doc.name}</span>
                          <DownloadSimple size={18} className="text-muted-foreground group-hover:text-primary shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subcategories/tags */}
                {provider.subcategories?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-3">Specialties</h3>
                    <div className="flex gap-2 flex-wrap">
                      {provider.subcategories.map((s, i) => (
                        <span key={s} className="rounded-full bg-secondary text-primary text-xs font-medium px-3 py-1.5">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div className="space-y-4" data-testid="services-section">
                {provider.services?.length > 0 ? provider.services.map((svc, i) => (
                  <div key={svc.service_id} className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-6 flex items-center justify-between fade-up" style={{ animationDelay: `${(i % 8) * 50}ms` }}>
                    <div>
                      <h3 className="font-display font-semibold text-foreground">{svc.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1 max-w-md leading-relaxed">{svc.description}</p>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      {svc.price_type === 'quote' ? (
                        <div className="text-sm font-medium text-primary">Custom quote</div>
                      ) : svc.price_type === 'range' ? (
                        <>
                          <div className="text-xl font-display font-semibold text-foreground tabular-nums">${svc.price_min}–${svc.price_max}</div>
                          <span className="text-xs text-muted-foreground">{SERVICE_UNIT_LABEL[svc.price_type]}</span>
                        </>
                      ) : (
                        <>
                          <div className="text-xl font-display font-semibold text-foreground tabular-nums">${svc.base_price}</div>
                          <span className="text-xs text-muted-foreground">{SERVICE_UNIT_LABEL[svc.price_type] || 'per event'}</span>
                        </>
                      )}
                    </div>
                  </div>
                )) : <p className="text-sm text-muted-foreground">No services listed yet.</p>}
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div data-testid="reviews-section">
                {/* Rating summary */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Star size={28} weight="fill" className="text-amber-400" />
                    <span className="text-4xl font-display font-semibold text-foreground tabular-nums">{provider.rating_avg}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{provider.review_count} Review{provider.review_count === 1 ? '' : 's'}</p>
                    {recPct !== null && <p className="text-xs text-muted-foreground">Recommended by {recPct}% of customers</p>}
                  </div>
                </div>
                {/* Review cards */}
                <div className="space-y-4">
                  {provider.reviews?.length > 0 ? provider.reviews.map((rev, ri) => (
                    <div key={rev.review_id} className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 fade-up" style={{ animationDelay: `${(ri % 8) * 50}ms` }}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 bg-secondary rounded-full flex items-center justify-center text-sm font-medium text-primary">
                          {rev.customer?.name?.[0] || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{rev.customer?.name || 'Customer'}</p>
                          <p className="text-[11px] text-muted-foreground">{new Date(rev.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 mb-2">
                        {[1,2,3,4,5].map(i => <Star key={i} size={13} weight="fill" className={i <= rev.rating ? 'text-amber-400' : 'text-border'} />)}
                        <span className="text-xs font-medium text-muted-foreground ml-1 tabular-nums">{rev.rating}.0</span>
                      </div>
                      {rev.comment && <p className="text-sm text-muted-foreground leading-relaxed">{rev.comment}</p>}
                    </div>
                  )) : <p className="text-sm text-muted-foreground">No reviews yet.</p>}
                </div>
              </div>
            )}

            {/* FAQ Tab */}
            {activeTab === 'faq' && (
              <div className="space-y-4" data-testid="faq-section">
                {[
                  { q: 'What areas do you serve?', a: `We serve ${provider.city} and surrounding areas within a ${provider.service_radius_km || 25}km radius.` },
                  { q: 'How far in advance should I book?', a: 'We recommend booking at least 2-4 weeks in advance to ensure availability, especially for weekends and holidays.' },
                  { q: 'What is your cancellation policy?', a: describePolicy(provider) },
                  { q: 'Do you offer packages?', a: (() => { const pr = providerPrice(provider);
                    if (pr.isQuote) return 'Pricing is by custom quote — request a quote and they’ll send a price for your event.';
                    if (pr.isRange) return `Yes! See the services tab — pricing ranges ${pr.display}.`;
                    return `Yes! Check our services tab for available packages starting at $${pr.amount}.`;
                  })() },
                ].map((faq, i) => (
                  <div key={i} className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 fade-up" style={{ animationDelay: `${(i % 8) * 50}ms` }}>
                    <h4 className="text-sm font-semibold text-foreground mb-2">{faq.q}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                  </div>
                ))}
                <div className="bg-secondary/70 border border-primary/15 rounded-2xl p-5 flex items-center justify-between">
                  <span className="text-sm text-foreground">Any other questions?</span>
                  <Link to={user ? `/messages/${provider.user_id}` : '/auth'} className="bg-primary text-primary-foreground px-5 py-2.5 min-h-[44px] inline-flex items-center text-xs font-semibold rounded-xl btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer">Ask {name.split(' ')[0]}</Link>
                </div>
              </div>
            )}
          </div>

          {/* ═══ Right Sidebar ═══ */}
          <div className="space-y-4 md:sticky md:top-20 self-start">
            {/* Pricing + Contact card */}
            <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6" data-testid="booking-sidebar">
              {(() => { const pr = providerPrice(provider); return (
              <div className="mb-5">
                {pr.isQuote ? (
                  <>
                    <span className="text-sm font-medium tracking-widest uppercase text-primary">Pricing</span>
                    <div className="text-4xl font-display font-semibold text-foreground mt-1">Custom quote</div>
                    <span className="text-sm text-muted-foreground">Request a quote for pricing</span>
                  </>
                ) : (
                  <>
                    <span className="text-sm font-medium tracking-widest uppercase text-primary">Starting from</span>
                    <div className="text-4xl font-display font-semibold text-foreground mt-1 tabular-nums">${pr.amount}</div>
                    {pr.suffixLong && <span className="text-sm text-muted-foreground">{pr.suffixLong}</span>}
                  </>
                )}
              </div>
              ); })()}

              {providerPrice(provider).isQuote ? (
                /* Quote-only: the quote request IS the primary action. */
                <button onClick={() => (user ? setQuoteOpen(true) : navigate('/auth'))}
                  className="flex items-center justify-center w-full bg-primary text-primary-foreground text-center h-11 text-sm font-semibold rounded-xl btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer mb-3" data-testid="book-now-button">
                  Request a Quote
                </button>
              ) : (
                <>
                  {provider.services?.[0] && (
                    <Link to={user ? `/checkout/${provider.profile_id}/${provider.services[0].service_id}` : '/auth'}
                      className="flex items-center justify-center w-full bg-primary text-primary-foreground text-center h-11 text-sm font-semibold rounded-xl btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer mb-3" data-testid="book-now-button">
                      Request Booking
                    </Link>
                  )}
                  <button onClick={() => (user ? setQuoteOpen(true) : navigate('/auth'))}
                    className="flex items-center justify-center w-full border border-primary/40 bg-secondary/50 text-primary text-center h-11 text-sm font-semibold rounded-xl btn-anim hover:bg-primary hover:text-primary-foreground cursor-pointer mb-3" data-testid="request-quote-button">
                    Request a custom quote
                  </button>
                </>
              )}

              {user && (
                <Link to={`/messages/${provider.user_id}`}
                  className="flex items-center justify-center w-full border border-border bg-card text-foreground text-center h-11 text-sm font-medium rounded-xl btn-anim hover:bg-secondary/60 cursor-pointer mb-4" data-testid="message-provider-button">
                  <ChatCircle size={14} className="inline mr-1.5" /> Message
                </Link>
              )}

              {(provider.deposit_pct === 25 || provider.deposit_pct === 50) && (
                <p className="text-[11px] text-primary bg-secondary/60 rounded-lg px-3 py-2 mb-3" data-testid="deposit-note">
                  Reserve with a {provider.deposit_pct}% deposit — balance due 7 days before your event.
                </p>
              )}

              <div className="space-y-3 pt-4 border-t border-border/70">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Users size={13} className="text-primary" /> {bookCount > 0 ? `${bookCount} booking${bookCount === 1 ? '' : 's'} completed on Bookr` : 'New on Bookr — be among the first to book'}</div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Star size={13} weight="fill" className="text-amber-400" /> Highly recommended in your area</div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><MapPin size={13} className="text-primary" /> {provider.city} &middot; {provider.service_radius_km || 25}km radius</div>
                <div className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="policy-note">
                  <ShieldCheck size={13} className="text-primary shrink-0 mt-0.5" />
                  <span><span className="font-medium text-foreground">{policy.label} cancellation.</span> {describePolicy(provider)}</span>
                </div>
                <Link to="/trust" className="flex items-center gap-2 text-xs text-primary font-medium hover:underline" data-testid="guarantee-link">
                  <ShieldCheck size={13} weight="fill" /> Covered by the Bookr Guarantee
                </Link>
              </div>
            </div>

            {/* Availability Calendar — right under pricing, where booking decisions happen */}
            <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5">
              <AvailabilityCalendar providerUserId={provider.user_id} profileId={provider.profile_id} serviceId={provider.services?.[0]?.service_id || ''} />
            </div>

            <TrustStrip />
          </div>
        </div>
      </div>
    </div>
  );
}
